// Mock data for Loyalty > Digital Wallet (WinePik digital wallets).
// Merchant-facing copy never names the pass provider.

export interface WalletTierDesign {
  id: string;
  name: string;
  /** Hex colours */
  background: string;
  text: string;
  label: string;
  /** CSS gradient used as a stand-in for the uploaded banner image */
  banner: string;
  bannerFileName: string;
  members: number;
}

export const walletTiers: WalletTierDesign[] = [{
  id: 'enthusiast',
  name: 'Enthusiast',
  background: '#1F2937',
  text: '#FFFFFF',
  label: '#CBD5E1',
  banner: 'linear-gradient(135deg, #7F1D1D 0%, #B91C1C 55%, #F87171 100%)',
  bannerFileName: 'vineyard-autumn.jpg',
  members: 774
}, {
  id: 'connoisseur',
  name: 'Connoisseur',
  background: '#3B0764',
  text: '#FFFFFF',
  label: '#E9D5FF',
  banner: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 60%, #C4B5FD 100%)',
  bannerFileName: 'cellar-barrels.jpg',
  members: 299
}, {
  id: 'sommelier',
  name: 'Sommelier',
  background: '#78350F',
  text: '#FFFFFF',
  label: '#FDE68A',
  banner: 'linear-gradient(135deg, #92400E 0%, #D97706 60%, #FCD34D 100%)',
  bannerFileName: 'golden-hour-rows.jpg',
  members: 137
}, {
  id: 'vintner',
  name: 'Vintner',
  background: '#0B0B0B',
  text: '#F5F5F4',
  label: '#D6D3D1',
  banner: 'linear-gradient(135deg, #111827 0%, #374151 55%, #9CA3AF 100%)',
  bannerFileName: 'museum-release.jpg',
  members: 37
}];

export const walletSample = {
  fullName: 'Sarah Johnson',
  points: '2,450',
  rewards: '2',
  memberSince: 'Mar 2024',
  club: 'Club member',
  benefits: '10% off Estate wines, free shipping over $150',
  rewardsBreakdown: 'Free tasting for two, $20 off your next order',
  memberId: 'WP-104582'
};

export function nextTierName(tierId: string) {
  const i = walletTiers.findIndex(t => t.id === tierId);
  if (i < 0 || i === walletTiers.length - 1) return 'Top tier';
  return walletTiers[i + 1].name;
}

// Analytics (last 30 days)
export const walletKpis = {
  issued: 1247,
  inWallet: 862,
  apple: 571,
  google: 318,
  both: 27,
  removed: 41,
  loyaltyMembers: 1247
};

export const walletTrend = Array.from({
  length: 30
}).map((_, i) => {
  const day = new Date(2026, 9, 6 + i);
  const base = 8 + Math.round(6 * Math.sin(i / 3.2)) + (i > 20 ? 6 : 0);
  return {
    date: day.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    }),
    issued: base + 4,
    apple: Math.round(base * 0.62),
    google: Math.round(base * 0.35),
    removed: i % 6 === 0 ? 2 : i % 4 === 0 ? 1 : 0
  };
});

export type NotificationStatus = 'Scheduled' | 'Repeating' | 'Sending' | 'Sent' | 'Failed' | 'Cancelled';

export interface WalletNotification {
  id: string;
  title: string;
  message: string;
  audience: string;
  when: string;
  status: NotificationStatus;
  passes: number;
  appleSends: number;
  googleSends: number;
  repeat?: string;
}

export const walletNotifications: WalletNotification[] = [{
  id: 'n6',
  title: 'Museum release weekend',
  message: 'Our 2012 Shiraz is back for two days only. Vintner and Sommelier members get first access from 10am Saturday.',
  audience: 'Tiers: Sommelier, Vintner',
  when: '8 Nov 2026, 9:00 am',
  status: 'Scheduled',
  passes: 174,
  appleSends: 0,
  googleSends: 0
}, {
  id: 'n5',
  title: 'Friday tastings are on',
  message: 'Seated tastings every Friday from 4pm. Book ahead and earn points on your visit.',
  audience: 'Everyone with a pass',
  when: 'Every Friday, 11:00 am',
  status: 'Repeating',
  passes: 862,
  appleSends: 0,
  googleSends: 0,
  repeat: 'Weekly on Friday, 6 of 12 sent'
}, {
  id: 'n4',
  title: 'Hi {{firstName}}, a reward is waiting',
  message: 'You have {{rewards}} rewards ready to use. Open your pass to see them.',
  audience: 'Rewards available: 1 or more',
  when: '1 Nov 2026, 10:00 am',
  status: 'Sent',
  passes: 318,
  appleSends: 214,
  googleSends: 112
}, {
  id: 'n3',
  title: 'Spring release is here',
  message: 'Our new Riesling and rosé have landed. Members get early access in the shop this week.',
  audience: 'Everyone with a pass',
  when: '24 Oct 2026, 9:30 am',
  status: 'Sent',
  passes: 845,
  appleSends: 561,
  googleSends: 309
}, {
  id: 'n2',
  title: 'Long weekend hours',
  message: 'The cellar door is open 10am to 5pm all long weekend.',
  audience: 'Club members',
  when: '18 Oct 2026, 8:00 am',
  status: 'Cancelled',
  passes: 402,
  appleSends: 0,
  googleSends: 0
}, {
  id: 'n1',
  title: 'Welcome to your new member pass',
  message: 'Show this pass at the cellar door to earn points on every visit.',
  audience: 'Everyone with a pass',
  when: '10 Oct 2026, 12:00 pm',
  status: 'Sent',
  passes: 790,
  appleSends: 512,
  googleSends: 297
}];

export interface WalletLocation {
  id: string;
  name: string;
  address: string;
  message: string;
}

export const walletLocations: WalletLocation[] = [{
  id: 'l1',
  name: 'Cellar door',
  address: '120 Vineyard Road, Riddells Creek VIC 3431',
  message: 'Welcome back! Show your pass to earn points on your visit.'
}, {
  id: 'l2',
  name: 'City tasting room',
  address: '45 Flinders Lane, Melbourne VIC 3000',
  message: 'You are close to our tasting room. Pop in and show your pass.'
}];

export const personalisationFields = [{
  key: 'firstName',
  label: 'First name'
}, {
  key: 'points',
  label: 'Points'
}, {
  key: 'tier',
  label: 'Tier'
}, {
  key: 'nextTier',
  label: 'Next tier'
}, {
  key: 'rewards',
  label: 'Rewards available'
}];

export function fillPlaceholders(text: string) {
  return text.replace(/{{firstName}}/g, 'Sarah').replace(/{{points}}/g, walletSample.points).replace(/{{tier}}/g, 'Connoisseur').replace(/{{nextTier}}/g, 'Sommelier').replace(/{{rewards}}/g, walletSample.rewards);
}
